import { useEffect } from 'react'
import { Box, Container, Typography, Stack, CircularProgress } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { COLOR_PRIMARY, NAVBAR_BG, DARK_700, BG_LIGHT, CHECKCIRCLE_SIZE } from '../theme'
import { FancyGoldButton } from '../components/FancyGoldButton'
import PageMeta from '../components/PageMeta'
import { DEMO_BOOKING_URL, loadDemoBookingScript } from '../common/common'

const expectations = [
    'A live walkthrough of the platform with a member of our team',
    'Alert setup tailored to your keywords, industry and regions',
    'Honest answers on fit, pricing and next steps',
]

export default function BookDemo() {
    // The booking widget resizes its iframe via this script, so it must load after the iframe mounts
    useEffect(() => {
        loadDemoBookingScript()
    }, [])

    return (
        <Box>
            <PageMeta
                title="Book a Demo"
                description="Book a live demo of Contract Finder Pro. Pick a time that suits you and a member of our team will walk you through tender alerts, market analytics and AI bid writing."
                canonical="/book-demo"
            />
            {/* Hero */}
            <Box sx={{
                background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_700} 100%)`,
                pt: { xs: 4, md: 6 },
                pb: { xs: 4, md: 5 },
                textAlign: 'center',
            }}>
                <Container maxWidth="md">
                    <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '48px' }, mb: 2 }}>
                        Book a Demo
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '18px' }}>
                        Pick a time that suits you and we will show you how Contract Finder Pro finds, qualifies and helps you win public sector work
                    </Typography>
                </Container>
            </Box>

            {/* What to expect + booking widget */}
            <Box sx={{ backgroundColor: BG_LIGHT, py: { xs: 3, md: 5 } }}>
                <Container maxWidth="md">
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={{ xs: 1.5, md: 4 }}
                        justifyContent="center"
                        sx={{ mb: { xs: 3, md: 4 } }}
                    >
                        {expectations.map(point => (
                            <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: 260, mx: { xs: 'auto', md: 0 } }}>
                                <CheckCircleIcon sx={{ fontSize: CHECKCIRCLE_SIZE, color: COLOR_PRIMARY, mt: 0.3, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                            </Box>
                        ))}
                    </Stack>
                </Container>

                {/* Slightly wider than the copy container: below 768px of iframe width the widget
                    switches to its single-column mobile flow with an extra date-then-time step */}
                <Container maxWidth={false} sx={{ maxWidth: 848 }}>
                    {/* The embed script hides the iframe (position absolute, offscreen) until the widget
                        inside reports ready, so the wrapper must hold the space and the spinner sits
                        behind the iframe rather than being toggled by onLoad, which fires too early */}
                    <Box sx={{ position: 'relative', minHeight: 420 }}>
                        <Stack
                            alignItems="center"
                            spacing={2}
                            sx={{ position: 'absolute', inset: 0, pt: { xs: 8, md: 12 } }}
                        >
                            <CircularProgress sx={{ color: COLOR_PRIMARY }} />
                            <Typography color="text.secondary">Loading the booking calendar...</Typography>
                        </Stack>
                        <Box
                            component="iframe"
                            src={DEMO_BOOKING_URL}
                            id="nqDvluEGCng5KAEtPCqv_booking"
                            title="Book a demo"
                            scrolling="no"
                            allow="payment"
                            sx={{
                                position: 'relative', zIndex: 1, width: '100%', minHeight: 420,
                                border: 'none', overflow: 'hidden', display: 'block', backgroundColor: 'white',
                            }}
                        />
                    </Box>
                </Container>

                <Container maxWidth="md">
                    {/* Trial alternative */}
                    <Stack alignItems="center" spacing={2} sx={{ mt: { xs: 4, md: 6 }, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            Prefer to explore at your own pace? Try the platform free for 14 days and cancel anytime
                        </Typography>
                        <FancyGoldButton to="/register">
                            Start Your Free Trial
                        </FancyGoldButton>
                    </Stack>
                </Container>
            </Box>
        </Box>
    )
}
